import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericListPage } from '../../components/generic';
import { useTasks, Task } from './useData';
import { moduleConfig, getColumns, getToolbarIcon, getTableActions } from './config';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTaskNotificationEnabled } from '../../modules/settings/useData';
import api from '../../services/api';

const List: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { enabled: taskNotificationEnabled, mutate: mutateTaskNotification } = useTaskNotificationEnabled();
  const { mutate } = useTasks();

  // Revalidate when opening task list so "Notify client" visibility matches current setting
  useEffect(() => {
    mutateTaskNotification();
  }, [mutateTaskNotification]);

  const handleNotifyClient = useCallback(
    async (taskId: number) => {
      try {
        await api.post(`/crm-tasks/${taskId}/send-client-notification`);
        showSuccess?.('Notification sent to client.');
        mutate();
      } catch (err: any) {
        showError?.(err?.response?.data?.message ?? 'Failed to send notification to client.');
      }
    },
    [showSuccess, showError, mutate]
  );

  const now = new Date();
  const supportTaskDefaults = {
    taskAssignedFrom: user?.id,
    taskAssignedTo: user?.id,
    taskCategoryId: 2,
    taskStatus: 1,
    taskDate: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`,
    taskStartDate: now.toISOString(),
  };

  const handleAddSupportTask = () => {
    navigate(moduleConfig.module.route.new, {
      state: { supportTaskDefaults },
    });
  };

  return (
    <GenericListPage<Task>
      useData={useTasks}
      config={{
        ...moduleConfig,
        getColumns,
        getToolbarIcon,
        getTableActions: (item: Task, onDelete: (id: number) => void, colors?: unknown, listContext?: unknown, options?: { canDelete?: boolean }) =>
          getTableActions(item, onDelete, colors, taskNotificationEnabled ? handleNotifyClient : undefined, options),
        toolbar: {
          ...moduleConfig.toolbar,
          actions: [
            ...(moduleConfig.toolbar.actions ?? []),
            {
              key: 'add-support-task',
              icon: 'add-support-task',
              title: 'Add Support Task',
              showTitle: false,
              variant: 'primary' as const,
              onClick: handleAddSupportTask,
              className: 'min-w-[2.5rem] w-10',
            },
          ],
          viewToggle: {
            enabled: false,
            views: [
              { key: 'list', label: 'List View', icon: 'list', href: '/crm_tasks' },
            ],
          },
        },
      }}
    />
  );
};

export default List;

